import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronRight, Pill } from "lucide-react";

import api from "../lib/api";
import ProductCard from "../components/storefront/ProductCard";

const SITE_URL = "https://farmakeiokatirtzoglou.com";

// =========================================================
// HELPERS
// =========================================================

function normalizePath(pathname) {
  return pathname
    .replace(/^\/+|\/+$/g, "")
    .split("/")
    .filter(Boolean);
}

// =========================================================
// CATEGORY PAGE
// =========================================================

export default function CategoryPage() {
  const location = useLocation();

  /*
   * Με το Route:
   *
   * /category/*
   *
   * παίρνουμε ολόκληρο το pathname.
   *
   * Παραδείγματα:
   *
   * /category/face-care
   * -> ["category", "face-care"]
   *
   * /category/face-care/cleansers
   * -> ["category", "face-care", "cleansers"]
   */

  const pathSegments = useMemo(() => {
    const segments = normalizePath(location.pathname);

    if (segments[0] === "category") {
      return segments.slice(1);
    }

    return [];
  }, [location.pathname]);

  const categoryPath = useMemo(() => {
    return pathSegments.join("/");
  }, [pathSegments]);

  const currentSlug = useMemo(() => {
    if (pathSegments.length === 0) {
      return "";
    }

    return pathSegments[pathSegments.length - 1];
  }, [pathSegments]);

  const [category, setCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // =========================================================
  // LOAD DATA
  // =========================================================

  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      setLoading(true);

      try {
        const [categoriesResponse, productsResponse] =
          await Promise.all([
            api.get("/categories"),
            api.get("/products"),
          ]);

        if (!mounted) {
          return;
        }

        const loadedCategories =
          categoriesResponse.data || [];

        const loadedProducts =
          productsResponse.data || [];

        setCategories(loadedCategories);
        setProducts(loadedProducts);

        /*
         * -----------------------------------------------------
         * FIND CATEGORY
         * -----------------------------------------------------
         *
         * Πρώτα ψάχνουμε το τελευταίο slug του URL.
         *
         * Υποστηρίζουμε:
         *
         * 1. current slug
         * 2. legacy_slugs
         *
         * Έτσι παλιά URLs συνεχίζουν να αναγνωρίζονται.
         */

        const foundCategory =
          loadedCategories.find((c) => {
            if (c.slug === currentSlug) {
              return true;
            }

            if (
              Array.isArray(c.legacy_slugs) &&
              c.legacy_slugs.includes(currentSlug)
            ) {
              return true;
            }

            return false;
          });

        /*
         * -----------------------------------------------------
         * VALIDATE NESTED PATH
         * -----------------------------------------------------
         *
         * Αν έχουμε:
         *
         * /category/face-care/cleansers
         *
         * πρέπει το cleansers να είναι πραγματικά παιδί
         * του face-care.
         *
         * Έτσι αποφεύγουμε λάθος URL όπως:
         *
         * /category/face-care/random-category
         */

        if (foundCategory && pathSegments.length > 1) {
          let validPath = true;

          let expectedParentId = null;

          for (let i = 0; i < pathSegments.length; i++) {
            const segment = pathSegments[i];

            const pathCategory =
              loadedCategories.find((c) => {
                if (c.slug === segment) {
                  return true;
                }

                if (
                  Array.isArray(c.legacy_slugs) &&
                  c.legacy_slugs.includes(segment)
                ) {
                  return true;
                }

                return false;
              });

            if (!pathCategory) {
              validPath = false;
              break;
            }

            if (i === 0) {
              /*
               * Το πρώτο επίπεδο πρέπει να είναι
               * top-level category.
               */

              if (pathCategory.parent_id) {
                validPath = false;
                break;
              }
            } else {
              /*
               * Κάθε επόμενο επίπεδο πρέπει να είναι
               * παιδί του προηγούμενου.
               */

              if (
                pathCategory.parent_id !==
                expectedParentId
              ) {
                validPath = false;
                break;
              }
            }

            expectedParentId = pathCategory.id;
          }

          if (!validPath) {
            setCategory(null);
          } else {
            setCategory(foundCategory);
          }
        } else {
          setCategory(foundCategory || null);
        }
      } catch (error) {
        console.error(
          "CategoryPage error:",
          error
        );

        if (mounted) {
          setCategory(null);
          setCategories([]);
          setProducts([]);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      mounted = false;
    };
  }, [currentSlug, pathSegments]);

  // =========================================================
  // CATEGORY MAP
  // =========================================================

  const categoryMap = useMemo(() => {
    const map = {};

    categories.forEach((c) => {
      map[c.id] = c;
    });

    return map;
  }, [categories]);

  // =========================================================
  // GET CATEGORY PATH
  // =========================================================

  /*
   * Δημιουργεί το πλήρες SEO URL μιας κατηγορίας
   * χρησιμοποιώντας τους γονείς της.
   *
   * Παράδειγμα:
   *
   * face-care
   *
   * -> /category/face-care
   *
   * cleansers με parent face-care
   *
   * -> /category/face-care/cleansers
   */

  const getCategoryPath = (categoryItem) => {
    if (!categoryItem) {
      return "/category";
    }

    const parts = [];
    const visited = new Set();

    let current = categoryItem;

    while (
      current &&
      !visited.has(current.id)
    ) {
      visited.add(current.id);

      if (current.slug) {
        parts.unshift(current.slug);
      }

      if (!current.parent_id) {
        break;
      }

      current = categoryMap[current.parent_id];
    }

    return `/category/${parts.join("/")}`;
  };

  // =========================================================
  // CHILD CATEGORIES
  // =========================================================

  const childCategories = useMemo(() => {
    if (!category) {
      return [];
    }

    return categories.filter(
      (c) => c.parent_id === category.id
    );
  }, [categories, category]);

  // =========================================================
  // GET CATEGORY TREE IDS
  // =========================================================

  const getCategoryTreeIds = (categoryId) => {
    const ids = [];
    const queue = [categoryId];
    const visited = new Set();

    while (queue.length > 0) {
      const currentId = queue.shift();

      if (
        !currentId ||
        visited.has(currentId)
      ) {
        continue;
      }

      visited.add(currentId);
      ids.push(currentId);

      categories.forEach((c) => {
        if (
          c.parent_id === currentId &&
          !visited.has(c.id)
        ) {
          queue.push(c.id);
        }
      });
    }

    return ids;
  };

  // =========================================================
  // GET PRODUCT CATEGORY IDS
  // =========================================================

  const getProductCategoryIds = (product) => {
    if (
      Array.isArray(product.category_ids) &&
      product.category_ids.length > 0
    ) {
      return product.category_ids;
    }

    if (product.category_id) {
      return [product.category_id];
    }

    return [];
  };

  // =========================================================
  // PRODUCTS BELONGING TO CATEGORY
  // =========================================================

  const categoryProducts = useMemo(() => {
    if (!category) {
      return [];
    }

    const validCategoryIds =
      getCategoryTreeIds(category.id);

    return products.filter((product) => {
      const productCategoryIds =
        getProductCategoryIds(product);

      return productCategoryIds.some((id) =>
        validCategoryIds.includes(id)
      );
    });
  }, [
    category,
    products,
    categories,
  ]);

  // =========================================================
  // BREADCRUMBS
  // =========================================================

  const breadcrumbs = useMemo(() => {
    if (!category) {
      return [];
    }

    const result = [];
    const visited = new Set();

    let current = category;

    while (
      current &&
      !visited.has(current.id)
    ) {
      visited.add(current.id);

      result.unshift(current);

      if (!current.parent_id) {
        break;
      }

      current =
        categoryMap[current.parent_id];
    }

    return result;
  }, [
    category,
    categoryMap,
  ]);

  // =========================================================
  // PAGE TITLE
  // =========================================================

  useEffect(() => {
    if (category) {
      document.title =
        `${category.name} | Φαρμακείο Κατιρτζόγλου`;
    } else if (currentSlug) {
      document.title =
        "Κατηγορία προϊόντων | Φαρμακείο Κατιρτζόγλου";
    }
  }, [category, currentSlug]);

  // =========================================================
  // META DESCRIPTION
  // =========================================================

  useEffect(() => {
    if (!category) {
      return;
    }

    const description =
      category.description?.trim() ||
      `Ανακαλύψτε προϊόντα στην κατηγορία ${category.name} από το Φαρμακείο Κατιρτζόγλου. Επιλεγμένα προϊόντα υγείας, ομορφιάς και περιποίησης.`;

    let meta = document.querySelector(
      'meta[name="description"]'
    );

    if (!meta) {
      meta = document.createElement("meta");

      meta.setAttribute(
        "name",
        "description"
      );

      document.head.appendChild(meta);
    }

    meta.setAttribute(
      "content",
      description
    );

    return () => {};
  }, [category]);

  // =========================================================
  // CANONICAL URL
  // =========================================================

  useEffect(() => {
    if (!category?.slug) {
      return;
    }

    /*
     * Το canonical δημιουργείται από ολόκληρη
     * την ιεραρχία της κατηγορίας.
     *
     * Παράδειγμα:
     *
     * /category/face-care
     *
     * ή
     *
     * /category/face-care/cleansers
     */

    const canonicalPath =
      getCategoryPath(category);

    const canonicalUrl =
      `${SITE_URL}${canonicalPath}`;

    let canonical =
      document.querySelector(
        'link[rel="canonical"]'
      );

    if (!canonical) {
      canonical = document.createElement(
        "link"
      );

      canonical.setAttribute(
        "rel",
        "canonical"
      );

      document.head.appendChild(
        canonical
      );
    }

    canonical.setAttribute(
      "href",
      canonicalUrl
    );

    return () => {};
  }, [
    category,
    categoryMap,
  ]);

  // =========================================================
  // BREADCRUMB STRUCTURED DATA
  // =========================================================

  useEffect(() => {
    if (!category) {
      return;
    }

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Αρχική",
          item: `${SITE_URL}/`,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Προϊόντα",
          item: `${SITE_URL}/shop`,
        },
        ...breadcrumbs.map(
          (item, index) => ({
            "@type": "ListItem",
            position: index + 3,
            name: item.name,
            item:
              `${SITE_URL}${getCategoryPath(
                item
              )}`,
          })
        ),
      ],
    };

    let script =
      document.getElementById(
        "category-breadcrumb-schema"
      );

    if (!script) {
      script =
        document.createElement(
          "script"
        );

      script.id =
        "category-breadcrumb-schema";

      script.type =
        "application/ld+json";

      document.head.appendChild(
        script
      );
    }

    script.textContent =
      JSON.stringify(
        breadcrumbSchema
      );

    return () => {
      const existing =
        document.getElementById(
          "category-breadcrumb-schema"
        );

      if (existing) {
        existing.remove();
      }
    };
  }, [
    category,
    breadcrumbs,
    categoryMap,
  ]);

  // =========================================================
  // CATEGORY STRUCTURED DATA
  // =========================================================

  useEffect(() => {
    if (!category) {
      return;
    }

    const description =
      category.description?.trim() ||
      `Ανακαλύψτε προϊόντα στην κατηγορία ${category.name} από το Φαρμακείο Κατιρτζόγλου.`;

    const canonicalPath =
      getCategoryPath(category);

    const categorySchema = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: category.name,
      description,
      url:
        `${SITE_URL}${canonicalPath}`,
      isPartOf: {
        "@type": "WebSite",
        name: "Φαρμακείο Κατιρτζόγλου",
        url: `${SITE_URL}/`,
      },
    };

    let script =
      document.getElementById(
        "category-page-schema"
      );

    if (!script) {
      script =
        document.createElement(
          "script"
        );

      script.id =
        "category-page-schema";

      script.type =
        "application/ld+json";

      document.head.appendChild(
        script
      );
    }

    script.textContent =
      JSON.stringify(
        categorySchema
      );

    return () => {
      const existing =
        document.getElementById(
          "category-page-schema"
        );

      if (existing) {
        existing.remove();
      }
    };
  }, [
    category,
    categoryMap,
  ]);

  // =========================================================
  // LOADING
  // =========================================================

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16">
        <p className="text-[#536D61]">
          Φόρτωση κατηγορίας...
        </p>
      </div>
    );
  }

  // =========================================================
  // CATEGORY NOT FOUND
  // =========================================================

  if (!category) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16">
        <div className="text-center">

          <Pill className="h-14 w-14 text-[#1E3A2F]/30 mx-auto mb-5" />

          <h1 className="font-serif-display text-3xl text-[#1E3A2F]">
            Η κατηγορία δεν βρέθηκε
          </h1>

          <p className="text-[#536D61] mt-3">
            Η κατηγορία που αναζητάτε
            δεν είναι διαθέσιμη.
          </p>

          <Link
            to="/shop"
            className="inline-flex mt-6 bg-[#1E3A2F] text-white px-6 py-3 rounded-full text-sm hover:bg-[#111C18]"
          >
            Δείτε όλα τα προϊόντα
          </Link>

        </div>
      </div>
    );
  }

  // =========================================================
  // PAGE
  // =========================================================

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10">

      {/* =====================================================
          BREADCRUMBS
      ====================================================== */}

      <nav
        aria-label="Breadcrumb"
        className="flex items-center gap-1.5 text-sm text-[#536D61] mb-7 flex-wrap"
      >

        <Link
          to="/"
          className="hover:text-[#1E3A2F]"
        >
          Αρχική
        </Link>

        <ChevronRight className="h-3.5 w-3.5 shrink-0" />

        <Link
          to="/shop"
          className="hover:text-[#1E3A2F]"
        >
          Προϊόντα
        </Link>

        {breadcrumbs.map(
          (item, index) => (
            <React.Fragment
              key={item.id}
            >

              <ChevronRight className="h-3.5 w-3.5 shrink-0" />

              {index ===
              breadcrumbs.length - 1 ? (
                <span className="text-[#1E3A2F] font-medium">
                  {item.name}
                </span>
              ) : (
                <Link
                  to={getCategoryPath(
                    item
                  )}
                  className="hover:text-[#1E3A2F]"
                >
                  {item.name}
                </Link>
              )}

            </React.Fragment>
          )
        )}

      </nav>

      {/* =====================================================
          CATEGORY HEADER
      ====================================================== */}

      <header className="mb-10">

        <h1 className="font-serif-display text-4xl lg:text-5xl text-[#1E3A2F]">
          {category.name}
        </h1>

        {category.description && (
          <p className="mt-4 max-w-3xl text-[#536D61] leading-7">
            {category.description}
          </p>
        )}

        <p className="mt-3 text-sm text-[#536D61]">
          {categoryProducts.length}{" "}
          {categoryProducts.length === 1
            ? "προϊόν"
            : "προϊόντα"}
        </p>

      </header>

      {/* =====================================================
          SUBCATEGORIES
      ====================================================== */}

      {childCategories.length > 0 && (
        <section className="mb-10">

          <h2 className="font-serif-display text-2xl text-[#1E3A2F] mb-5">
            Υποκατηγορίες
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">

            {childCategories.map(
              (child) => {

                const childProductCount =
                  products.filter(
                    (product) => {

                      const productCategoryIds =
                        getProductCategoryIds(
                          product
                        );

                      const validIds =
                        getCategoryTreeIds(
                          child.id
                        );

                      return productCategoryIds.some(
                        (id) =>
                          validIds.includes(
                            id
                          )
                      );
                    }
                  ).length;

                return (
                  <Link
                    key={child.id}
                    to={getCategoryPath(
                      child
                    )}
                    className="group border border-[#E5E9E4] rounded-2xl p-5 bg-white hover:border-[#1E3A2F] hover:bg-[#F8FAF6] transition-all"
                  >

                    <div className="flex items-center justify-between gap-4">

                      <div className="min-w-0">

                        <h3 className="text-[#1E3A2F] font-semibold">
                          {child.name}
                        </h3>

                        <p className="text-sm text-[#536D61] mt-1">
                          {childProductCount}{" "}
                          {childProductCount ===
                          1
                            ? "προϊόν"
                            : "προϊόντα"}
                        </p>

                      </div>

                      <ChevronRight className="h-5 w-5 text-[#536D61] group-hover:text-[#1E3A2F] shrink-0" />

                    </div>

                  </Link>
                );
              }
            )}

          </div>

        </section>
      )}

      {/* =====================================================
          PRODUCTS
      ====================================================== */}

      {categoryProducts.length > 0 ? (

        <section>

          <h2 className="font-serif-display text-2xl text-[#1E3A2F] mb-6">
            Προϊόντα {category.name}
          </h2>

          <div
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-8"
            data-testid="category-products-grid"
          >

            {categoryProducts.map(
              (product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                />
              )
            )}

          </div>

        </section>

      ) : (

        <div className="text-center py-16 border border-dashed border-[#E5E9E4] rounded-2xl">

          <Pill className="h-12 w-12 text-[#1E3A2F]/30 mx-auto mb-4" />

          <h2 className="font-serif-display text-2xl text-[#1E3A2F]">
            Δεν υπάρχουν προϊόντα
          </h2>

          <p className="text-[#536D61] text-sm mt-2">
            Δεν υπάρχουν διαθέσιμα προϊόντα
            σε αυτή την κατηγορία.
          </p>

          <Link
            to="/shop"
            className="inline-flex mt-6 bg-[#1E3A2F] text-white px-6 py-3 rounded-full text-sm hover:bg-[#111C18]"
          >
            Δείτε όλα τα προϊόντα
          </Link>

        </div>

      )}

    </div>
  );
}